import { AuthorizationState, Scope, System, User, UserToken } from '../types';
import { StorageAdapter, CreateUserInput, UserTokenWithProfile } from './interfaces';

export class InMemoryStorageAdapter implements StorageAdapter {
  private systems: Map<string, System> = new Map();
  private scopes: Map<string, Scope> = new Map();
  private users: Map<string, User> = new Map();
  private tokens: Map<string, UserToken> = new Map();
  private states: Map<string, AuthorizationState> = new Map();

  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  async createSystem(system: Omit<System, 'id' | 'createdAt' | 'updatedAt'>): Promise<System> {
    const newSystem: System = {
      ...system,
      id: this.generateId(),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    this.systems.set(newSystem.id, newSystem);
    return newSystem;
  }

  async getSystem(id: string): Promise<System | null> {
    return this.systems.get(id) || null;
  }

  async updateSystem(id: string, updates: Partial<System>): Promise<System> {
    const system = this.systems.get(id);
    if (!system) throw new Error('System not found');

    const updatedSystem = { ...system, ...updates, updatedAt: new Date() };
    this.systems.set(id, updatedSystem);
    return updatedSystem;
  }

  async deleteSystem(id: string): Promise<void> {
    this.systems.delete(id);
  }

  async listSystems(): Promise<System[]> {
    return Array.from(this.systems.values());
  }

  async createScope(scope: Omit<Scope, 'id'>): Promise<Scope> {
    const newScope: Scope = {
      ...scope,
      id: this.generateId(),
    };
    this.scopes.set(newScope.id, newScope);
    return newScope;
  }

  async getScope(id: string): Promise<Scope | null> {
    return this.scopes.get(id) || null;
  }

  async getScopesBySystem(systemId: string): Promise<Scope[]> {
    return Array.from(this.scopes.values()).filter((s) => s.systemId === systemId);
  }

  async updateScope(id: string, updates: Partial<Scope>): Promise<Scope> {
    const scope = this.scopes.get(id);
    if (!scope) throw new Error('Scope not found');

    const updatedScope = { ...scope, ...updates };
    this.scopes.set(id, updatedScope);
    return updatedScope;
  }

  async deleteScope(id: string): Promise<void> {
    this.scopes.delete(id);
  }

  async createUser(user: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    const newUser: User = {
      ...user,
      id: this.generateId(),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    this.users.set(newUser.id, newUser);
    return newUser;
  }

  async getOrCreateUser(input: CreateUserInput): Promise<User> {
    // First, try to find existing user by email
    if (input.email) {
      const existingUser = await this.findUserByEmail(input.systemId, input.email);
      if (existingUser) {
        return existingUser;
      }
    }

    // Then try to find by external ID
    if (input.externalId) {
      const existingUser = await this.findUserByExternalId(input.systemId, input.externalId);
      if (existingUser) {
        return existingUser;
      }
    }

    // Create new user if not found
    return this.createUser({
      systemId: input.systemId,
      metadata: {
        ...input.metadata,
        ...(input.email && { email: input.email }),
        ...(input.externalId && { externalId: input.externalId }),
      },
    });
  }

  async findUserByEmail(systemId: string, email: string): Promise<User | null> {
    const users = Array.from(this.users.values());
    return users.find((u) => u.systemId === systemId && u.metadata?.email === email) || null;
  }

  async findUserByExternalId(systemId: string, externalId: string): Promise<User | null> {
    const users = Array.from(this.users.values());
    return (
      users.find((u) => u.systemId === systemId && u.metadata?.externalId === externalId) || null
    );
  }

  async getUser(id: string): Promise<User | null> {
    return this.users.get(id) || null;
  }

  async getUsersBySystem(systemId: string): Promise<User[]> {
    return Array.from(this.users.values()).filter((u) => u.systemId === systemId);
  }

  async updateUser(id: string, updates: Partial<User>): Promise<User> {
    const user = this.users.get(id);
    if (!user) throw new Error('User not found');

    const updatedUser = { ...user, ...updates, updatedAt: new Date() };
    this.users.set(id, updatedUser);
    return updatedUser;
  }

  async deleteUser(id: string): Promise<void> {
    this.users.delete(id);
  }

  async saveToken(token: Omit<UserToken, 'id' | 'createdAt' | 'updatedAt'>): Promise<UserToken> {
    // Create new token
    const newToken: UserToken = {
      ...token,
      id: this.generateId(),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    this.tokens.set(newToken.id, newToken);
    return newToken;
  }

  async saveTokenWithEmailValidation(
    userId: string,
    systemId: string,
    scopeId: string,
    provider: string,
    email: string,
    token: Omit<UserToken, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<UserToken> {
    // Check if there's already a token with this email for this user/scope/provider
    const existingTokens = await this.findTokensByEmailInUserScopeProvider(
      userId,
      scopeId,
      provider,
      email,
    );

    if (existingTokens.length > 0) {
      // Replace existing token(s) with same email
      return this.replaceTokensByEmailInUserScopeProvider(userId, scopeId, provider, email, token);
    }

    // Create new token with email stored directly in the token
    const tokenWithEmail: Omit<UserToken, 'id' | 'createdAt' | 'updatedAt'> = {
      ...token,
      userId,
      systemId,
      scopeId,
      provider,
      email, // Store email directly in token
    };

    return this.saveToken(tokenWithEmail);
  }

  // Query by User (all levels)
  async getTokensByUser(userId: string): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter((t) => t.userId === userId);
  }

  async getTokensByUserAndScope(userId: string, scopeId: string): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) => t.userId === userId && t.scopeId === scopeId,
    );
  }

  async getTokensByUserAndProvider(userId: string, provider: string): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) => t.userId === userId && t.provider === provider,
    );
  }

  async getTokensByUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
  ): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) => t.userId === userId && t.scopeId === scopeId && t.provider === provider,
    );
  }

  // Query by System/Scope (cross-user)
  async getTokensByScope(systemId: string, scopeId: string): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) => t.systemId === systemId && t.scopeId === scopeId,
    );
  }

  async getTokensByProvider(systemId: string, provider: string): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) => t.systemId === systemId && t.provider === provider,
    );
  }

  async getTokensBySystem(systemId: string): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter((t) => t.systemId === systemId);
  }

  // Query by Email (cross-user, cross-provider)
  async findTokensByEmail(email: string, systemId: string): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) => t.systemId === systemId && t.email === email,
    );
  }

  async findTokensByEmailAndScope(
    email: string,
    systemId: string,
    scopeId: string,
  ): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) => t.systemId === systemId && t.scopeId === scopeId && t.email === email,
    );
  }

  async findTokensByEmailAndProvider(
    email: string,
    systemId: string,
    provider: string,
  ): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) => t.systemId === systemId && t.provider === provider && t.email === email,
    );
  }

  async findTokenByEmailScopeProvider(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<UserToken | null> {
    return (
      Array.from(this.tokens.values()).find(
        (t) =>
          t.systemId === systemId &&
          t.scopeId === scopeId &&
          t.provider === provider &&
          t.email === email,
      ) || null
    );
  }

  // Email validation within provider
  async findTokensByEmailInUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    email: string,
  ): Promise<UserToken[]> {
    return Array.from(this.tokens.values()).filter(
      (t) =>
        t.userId === userId &&
        t.scopeId === scopeId &&
        t.provider === provider &&
        t.email === email,
    );
  }

  async hasTokenWithEmailInUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    email: string,
  ): Promise<boolean> {
    const tokens = await this.findTokensByEmailInUserScopeProvider(
      userId,
      scopeId,
      provider,
      email,
    );
    return tokens.length > 0;
  }

  async replaceTokensByEmailInUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    email: string,
    newToken: Omit<UserToken, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<UserToken> {
    // Find and delete all existing tokens with this email in this user/scope/provider
    const existingTokens = await this.findTokensByEmailInUserScopeProvider(
      userId,
      scopeId,
      provider,
      email,
    );
    existingTokens.forEach((token) => {
      this.tokens.delete(token.id);
    });

    // Create the new token
    const tokenData = {
      ...newToken,
      userId,
      systemId: newToken.systemId,
      scopeId,
      provider,
    };

    return this.saveToken(tokenData);
  }

  // Token management
  async getTokenById(id: string): Promise<UserToken | null> {
    return this.tokens.get(id) || null;
  }

  async updateToken(id: string, updates: Partial<UserToken>): Promise<UserToken> {
    const token = this.tokens.get(id);
    if (!token) throw new Error('Token not found');

    const updatedToken = { ...token, ...updates, updatedAt: new Date() };
    this.tokens.set(id, updatedToken);
    return updatedToken;
  }

  async deleteToken(id: string): Promise<void> {
    this.tokens.delete(id);
  }

  async deleteTokensByUser(userId: string): Promise<void> {
    const tokensToDelete = Array.from(this.tokens.entries())
      .filter(([, token]) => token.userId === userId)
      .map(([id]) => id);

    tokensToDelete.forEach((id) => this.tokens.delete(id));
  }

  async deleteTokensByUserAndScope(userId: string, scopeId: string): Promise<void> {
    const tokensToDelete = Array.from(this.tokens.entries())
      .filter(([, token]) => token.userId === userId && token.scopeId === scopeId)
      .map(([id]) => id);

    tokensToDelete.forEach((id) => this.tokens.delete(id));
  }

  async deleteTokensByUserAndProvider(userId: string, provider: string): Promise<void> {
    const tokensToDelete = Array.from(this.tokens.entries())
      .filter(([, token]) => token.userId === userId && token.provider === provider)
      .map(([id]) => id);

    tokensToDelete.forEach((id) => this.tokens.delete(id));
  }

  async saveAuthorizationState(state: AuthorizationState): Promise<void> {
    this.states.set(state.state, state);
  }

  async getAuthorizationState(state: string): Promise<AuthorizationState | null> {
    return this.states.get(state) || null;
  }

  async deleteAuthorizationState(state: string): Promise<void> {
    this.states.delete(state);
  }

  async cleanupExpiredStates(expiryMs: number): Promise<void> {
    const now = new Date();
    const expiredStates = Array.from(this.states.entries())
      .filter(([, state]) => {
        const stateAge = now.getTime() - state.timestamp.getTime();
        return stateAge > expiryMs;
      })
      .map(([key]) => key);

    expiredStates.forEach((key) => this.states.delete(key));
  }

  // Enhanced token queries with user information
  async getTokensByUserWithProfile(userId: string): Promise<UserTokenWithProfile[]> {
    const tokens = await this.getTokensByUser(userId);
    const tokensWithProfile: UserTokenWithProfile[] = [];

    for (const token of tokens) {
      const user = this.users.get(token.userId);
      if (user) {
        tokensWithProfile.push({
          ...token,
          user: {
            email: user.metadata?.email,
            name: user.metadata?.name,
            externalId: user.metadata?.externalId,
            metadata: user.metadata,
          },
        });
      }
    }

    return tokensWithProfile;
  }

  async getTokensByUserAndScopeWithProfile(
    userId: string,
    scopeId: string,
  ): Promise<UserTokenWithProfile[]> {
    const tokens = await this.getTokensByUserAndScope(userId, scopeId);
    const tokensWithProfile: UserTokenWithProfile[] = [];

    for (const token of tokens) {
      const user = this.users.get(token.userId);
      if (user) {
        tokensWithProfile.push({
          ...token,
          user: {
            email: user.metadata?.email,
            name: user.metadata?.name,
            externalId: user.metadata?.externalId,
            metadata: user.metadata,
          },
        });
      }
    }

    return tokensWithProfile;
  }

  async getTokensByUserAndProviderWithProfile(
    userId: string,
    provider: string,
  ): Promise<UserTokenWithProfile[]> {
    const tokens = await this.getTokensByUserAndProvider(userId, provider);
    const tokensWithProfile: UserTokenWithProfile[] = [];

    for (const token of tokens) {
      const user = this.users.get(token.userId);
      if (user) {
        tokensWithProfile.push({
          ...token,
          user: {
            email: user.metadata?.email,
            name: user.metadata?.name,
            externalId: user.metadata?.externalId,
            metadata: user.metadata,
          },
        });
      }
    }

    return tokensWithProfile;
  }

  async getTokensByUserScopeProviderWithProfile(
    userId: string,
    scopeId: string,
    provider: string,
  ): Promise<UserTokenWithProfile[]> {
    const tokens = await this.getTokensByUserScopeProvider(userId, scopeId, provider);
    const tokensWithProfile: UserTokenWithProfile[] = [];

    for (const token of tokens) {
      const user = this.users.get(token.userId);
      if (user) {
        tokensWithProfile.push({
          ...token,
          user: {
            email: user.metadata?.email,
            name: user.metadata?.name,
            externalId: user.metadata?.externalId,
            metadata: user.metadata,
          },
        });
      }
    }

    return tokensWithProfile;
  }

  async getTokensByScopeWithProfile(
    systemId: string,
    scopeId: string,
  ): Promise<UserTokenWithProfile[]> {
    const tokens = await this.getTokensByScope(systemId, scopeId);
    const tokensWithProfile: UserTokenWithProfile[] = [];

    for (const token of tokens) {
      const user = this.users.get(token.userId);
      if (user) {
        tokensWithProfile.push({
          ...token,
          user: {
            email: user.metadata?.email,
            name: user.metadata?.name,
            externalId: user.metadata?.externalId,
            metadata: user.metadata,
          },
        });
      }
    }

    return tokensWithProfile;
  }

  async getTokensBySystemWithProfile(systemId: string): Promise<UserTokenWithProfile[]> {
    const tokens = await this.getTokensBySystem(systemId);
    const tokensWithProfile: UserTokenWithProfile[] = [];

    for (const token of tokens) {
      const user = this.users.get(token.userId);
      if (user) {
        tokensWithProfile.push({
          ...token,
          user: {
            email: user.metadata?.email,
            name: user.metadata?.name,
            externalId: user.metadata?.externalId,
            metadata: user.metadata,
          },
        });
      }
    }

    return tokensWithProfile;
  }
}
